import {
    CreatePVAnalysisVariantDto,
    PotentialAnalysisListItemDto,
    PotentialAnalysisSearchDto,
    PVAnalysisVariantDto,
    PvAnalysisVariationCalculatorResult,
    SalesPrintVariant,
} from './pv-analysis.dto';
import { PaginatedResponseDto } from 'src/infrastructure/admi-client/response.dto';
import { httpClient2 } from 'src/infrastructure/admi-client/http-client';
import { PVResultDto } from 'src/features/pv-analysis/pv-calculator.dto';

export const admiPVAnalysisService = {
    async listPVAnalysisIncludingVariants(
        search: PotentialAnalysisSearchDto,
    ): Promise<PaginatedResponseDto<PotentialAnalysisListItemDto>> {
        const response = await httpClient2.get<PaginatedResponseDto<PotentialAnalysisListItemDto>>(
            '/v1/potential-analysis',
            { params: search },
        );
        return response.data;
    },

    async createPVAnalysisVariant(dto: CreatePVAnalysisVariantDto): Promise<PVAnalysisVariantDto> {
        const formData = new FormData();
        formData.set('data', new Blob([JSON.stringify(dto)], { type: 'application/json' }));
        formData.set('mapImageData', dto.mapScreenshotImageData);

        const response = await httpClient2.post<PVAnalysisVariantDto>('/v1/potential-analysis/variants', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return response.data;
    },

    async createSalesPrintPreview(dto: CreatePVAnalysisVariantDto, salesPrintVariant: SalesPrintVariant) {
        const formData = new FormData();
        formData.set('data', new Blob([JSON.stringify(dto)], { type: 'application/json' }));
        formData.set('mapImageData', dto.mapScreenshotImageData);
        formData.set('salesPrintVariant', salesPrintVariant);

        const response = await httpClient2.post<Blob>('/v1/potential-analysis/sales-print/preview', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            responseType: 'blob',
        });

        return response.data;
    },

    async deletePVAnalysisVariant(uuid: string): Promise<void> {
        await httpClient2.delete(`/v1/potential-analysis/variants/${uuid}`);
    },

    async getPVAnalysisVariant(uuid: string): Promise<PVAnalysisVariantDto> {
        const response = await httpClient2.get<PVAnalysisVariantDto>(`/v1/potential-analysis/variants/${uuid}`);
        return response.data;
    },

    async getPVAnalysisMapScreenshotBlob(uuid: string): Promise<Blob> {
        const response = await httpClient2.get<Blob>(`/v1/potential-analysis/variants/${uuid}/map-screenshot`, {
            responseType: 'blob',
        });
        return response.data;
    },

    mapPvCalculatorDtoToAppDto(pvCalculatorResult: PVResultDto): PvAnalysisVariationCalculatorResult {
        return {
            amortizationYears: pvCalculatorResult.amortization_years,
            balanceAfter30Years: pvCalculatorResult.balance_30_years,
            initialInvestment: pvCalculatorResult.initial_investment,
            initialInvestmentInclFunding: pvCalculatorResult.initial_investment_incl_funding,
            profitLossAccumulated: pvCalculatorResult.profit_loss_accumulated,
            rateOfReturn: pvCalculatorResult.rate_of_return,
            annualProduction: pvCalculatorResult.design_kwh_per_year_ac,
            kwp: pvCalculatorResult.design_kwp,
            batteryCapacity: pvCalculatorResult.battery_capacity,
            selfConsumptionRate: pvCalculatorResult.ratio_own_use,
            selfSufficiencyRate: pvCalculatorResult.self_sufficiency_rate,
            loadSummary: pvCalculatorResult.load_summary,
            co2Savings: pvCalculatorResult.co2_savings,
            trees: pvCalculatorResult.trees,
            panelCount: pvCalculatorResult.panel_count,
            chartsDay: pvCalculatorResult.charts_day
                ? {
                      load: pvCalculatorResult.charts_day.load,
                      pvProduction: pvCalculatorResult.charts_day.pv_production,
                      selfConsumption: pvCalculatorResult.charts_day.self_consumption,
                      batteryCharge: pvCalculatorResult.charts_day.battery_charge,
                      batteryDischarge: pvCalculatorResult.charts_day.battery_discharge,
                  }
                : undefined,
            chartsYear: pvCalculatorResult.charts_year
                ? {
                      load: pvCalculatorResult.charts_year.load,
                      pvProduction: pvCalculatorResult.charts_year.pv_production,
                  }
                : undefined,
        };
    },
};
